import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/BrandLogo';

const TermsOfService: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-border">
        <div className="landing-container h-16 flex items-center justify-between">
          <Link to="/"><BrandLogo size="sm" /></Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            &larr; Back
          </Link>
        </div>
      </nav>

      {/* Content */}
      <article className="landing-container py-16 md:py-24 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">
          Terms of Service
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Last updated: March 2026
        </p>

        {/* 1 */}
        <Section title="1. Agreement to Terms">
          <P>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the
            Agentive Property platform accessible at agentiveproperty.ai (the
            &ldquo;Platform&rdquo;), operated by Agentive Property (&ldquo;we&rdquo;,
            &ldquo;us&rdquo;, &ldquo;our&rdquo;).
          </P>
          <P>
            By creating an account or using the Platform, you agree to be bound by these Terms. If
            you do not agree, you must not use the Platform. You must be at least 18 years of age to
            use the Platform.
          </P>
        </Section>

        {/* 2 */}
        <Section title="2. Description of Service">
          <P>
            Agentive Property is a software platform that connects property managers, tradies
            (tradespeople), tenants, and property owners to streamline property maintenance. The
            Platform provides:
          </P>
          <List items={[
            'Digital maintenance request submission by tenants',
            'AI-powered cost estimates and trade classification',
            'Competitive quoting from verified tradies',
            'Quote comparison and selection tools for property managers',
            'Owner/landlord approval workflows',
            'Job tracking and communication tools',
          ]} />
          <P>
            Agentive Property is a technology platform that facilitates connections between users. We
            are not a party to any agreement between property managers and tradies, and we do not
            provide trade services, property management services, or professional advice.
          </P>
        </Section>

        {/* 3 */}
        <Section title="3. Account Registration">
          <P>
            To use certain features of the Platform, you must register for an account. When
            registering, you agree to:
          </P>
          <List items={[
            'Provide accurate, current, and complete information',
            'Maintain and update your information to keep it accurate',
            'Keep your login credentials confidential and not share them with others',
            'Notify us immediately of any unauthorised use of your account',
            'Accept responsibility for all activity that occurs under your account',
          ]} />
          <P>
            We reserve the right to suspend or terminate accounts that contain inaccurate
            information, are used fraudulently, or violate these Terms.
          </P>
        </Section>

        {/* 4 */}
        <Section title="4. User Roles and Responsibilities">
          <Sub title="Property Managers">
            <P>
              Property managers are responsible for the accuracy of property and tenancy information
              they enter, obtaining appropriate authorisation from property owners before approving
              maintenance work, and selecting tradies and quotes in accordance with their duties to
              property owners.
            </P>
          </Sub>
          <Sub title="Tradies (Tradespeople)">
            <P>
              Tradies must hold all licences, certifications, and insurance required by applicable
              Australian federal and state laws for their trade. Tradies are responsible for the
              accuracy of their quotes, the quality of their work, and compliance with all applicable
              workplace health and safety legislation. By registering as a tradie, you represent and
              warrant that you are appropriately licensed and insured.
            </P>
          </Sub>
          <Sub title="Tenants and Property Owners">
            <P>
              Tenants must provide accurate descriptions and photographs when submitting maintenance
              requests and must provide reasonable access for approved maintenance work. Property
              owners are responsible for reviewing and approving quotes in a timely manner.
            </P>
          </Sub>
        </Section>

        {/* 5 */}
        <Section title="5. AI-Generated Estimates">
          <P>
            The Platform uses artificial intelligence to generate indicative cost estimates for
            maintenance requests. These AI estimates are provided for informational purposes only
            and:
          </P>
          <List items={[
            'Are not professional valuations, quotes, or guarantees of cost',
            'May not account for all site-specific conditions, complications, or requirements',
            'Should not be relied upon as the sole basis for making financial decisions',
            'Are supplemented by quotes from qualified tradies who assess the actual scope of work',
          ]} />
          <P>
            We do not warrant the accuracy of AI-generated estimates and accept no liability for
            decisions made in reliance upon them.
          </P>
        </Section>

        {/* 6 */}
        <Section title="6. Quoting and Payments">
          <P>
            The Platform facilitates the exchange of quotes between tradies and property managers.
            Any agreement for maintenance work is between the property manager (or property owner)
            and the tradie directly. We are not a party to those agreements.
          </P>
          <P>
            Payments processed through the Platform are handled by our third-party payment provider,
            Stripe. By using payment features, you agree to Stripe&rsquo;s terms of service in
            addition to these Terms. We do not store full credit card details on our servers.
          </P>
          <P>
            Disputes regarding the quality, scope, or payment of maintenance work should be resolved
            directly between the relevant parties. We may, at our discretion, assist with mediation
            but are not obligated to do so.
          </P>
        </Section>

        {/* 7 */}
        <Section title="7. Acceptable Use">
          <P>You agree not to use the Platform to:</P>
          <List items={[
            'Submit false, misleading, or fraudulent information, quotes, or requests',
            'Circumvent the Platform to arrange work or payments directly with users you connected with through the Platform',
            'Harass, abuse, threaten, or intimidate other users',
            'Impersonate another person or entity, or misrepresent your qualifications or licences',
            'Upload content that is illegal, defamatory, obscene, or infringes intellectual property rights',
            'Attempt to gain unauthorised access to the Platform, other accounts, or our systems',
            'Use automated tools (bots, scrapers) to access the Platform without our written permission',
            'Interfere with or disrupt the Platform or servers connected to it',
            'Use the Platform for any purpose that violates applicable Australian law',
          ]} />
        </Section>

        {/* 8 */}
        <Section title="8. Intellectual Property">
          <P>
            The Platform, including its design, features, code, content, logos, and trademarks, is
            owned by or licensed to Agentive Property and is protected by Australian and
            international intellectual property laws.
          </P>
          <P>
            We grant you a limited, non-exclusive, non-transferable, revocable licence to access and
            use the Platform for its intended purpose, subject to these Terms. You must not
            reproduce, modify, distribute, or create derivative works from any part of the Platform
            without our prior written consent.
          </P>
        </Section>

        {/* 9 */}
        <Section title="9. User Content">
          <P>
            You retain ownership of content you submit to the Platform, including maintenance
            request descriptions, photographs, quotes, and messages (&ldquo;User Content&rdquo;).
            By submitting User Content, you grant us a non-exclusive, worldwide, royalty-free
            licence to use, display, reproduce, and distribute that content as necessary to operate
            and improve the Platform.
          </P>
          <P>
            You are solely responsible for the accuracy and legality of your User Content. We
            reserve the right to remove any User Content that violates these Terms or applicable law.
          </P>
        </Section>

        {/* 10 */}
        <Section title="10. Limitation of Liability">
          <P>
            To the maximum extent permitted by law, Agentive Property and its officers, employees,
            and agents will not be liable for any indirect, incidental, special, consequential, or
            punitive damages, including loss of profits, data, or goodwill, arising out of or in
            connection with your use of the Platform.
          </P>
          <P>
            Our total aggregate liability for any claim arising from or related to these Terms or
            the Platform is limited to the amount you have paid us in the twelve (12) months
            preceding the claim, or AUD $100, whichever is greater.
          </P>
          <P>
            As the Platform connects property managers with independent tradies, we are not liable
            for the quality, safety, or timeliness of any maintenance work performed by tradies, or
            for any dispute between users.
          </P>
        </Section>

        {/* 11 */}
        <Section title="11. Australian Consumer Law">
          <P>
            Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right,
            or remedy conferred on you by the Australian Consumer Law (Schedule 2 of the Competition
            and Consumer Act 2010 (Cth)) or any other applicable law that cannot be excluded,
            restricted, or modified by agreement.
          </P>
          <P>
            If the Australian Consumer Law provides that there is a guarantee in relation to any
            goods or services we supply, and our liability for failing to comply with that guarantee
            cannot be excluded but may be limited, then our liability is limited to (at our
            election): supplying the services again, or paying the cost of having the services
            supplied again.
          </P>
        </Section>

        {/* 12 */}
        <Section title="12. Indemnification">
          <P>
            You agree to indemnify, defend, and hold harmless Agentive Property and its officers,
            employees, and agents from and against any claims, liabilities, damages, losses, costs,
            or expenses (including reasonable legal fees) arising out of or in connection with:
          </P>
          <List items={[
            'Your use of the Platform or violation of these Terms',
            'Your User Content or any information you provide through the Platform',
            'Your violation of any applicable law or the rights of a third party',
            'Any dispute between you and another user of the Platform',
          ]} />
        </Section>

        {/* 13 */}
        <Section title="13. Service Availability">
          <P>
            We strive to ensure the Platform is available at all times but do not guarantee
            uninterrupted or error-free access. We may modify, suspend, or discontinue any part of
            the Platform at any time, with or without notice, for maintenance, updates, or other
            reasons.
          </P>
          <P>
            We will make reasonable efforts to provide advance notice of planned maintenance that
            may affect service availability.
          </P>
        </Section>

        {/* 14 */}
        <Section title="14. Termination">
          <P>
            You may close your account at any time by contacting us at{' '}
            <a href="mailto:support@agentiveproperty.ai" className="text-accent hover:underline">
              support@agentiveproperty.ai
            </a>
            . We may suspend or terminate your account at any time if we reasonably believe you have
            violated these Terms, or for any other reason with reasonable notice.
          </P>
          <P>
            Upon termination, your right to use the Platform ceases immediately. We may retain
            certain information as required by law or for legitimate business purposes (such as
            resolving disputes or enforcing agreements). You may request deletion of your personal
            information in accordance with our Privacy Policy.
          </P>
        </Section>

        {/* 15 */}
        <Section title="15. Dispute Resolution">
          <P>
            If a dispute arises in connection with these Terms or the Platform, the parties agree to
            the following process:
          </P>
          <List items={[
            'Good faith negotiation: the parties will first attempt to resolve the dispute by direct negotiation within 14 days of written notice of the dispute',
            'Mediation: if the dispute is not resolved through negotiation, either party may refer the dispute to mediation administered by the Resolution Institute (or a similar body) in Queensland, Australia',
            'Litigation: if mediation is unsuccessful, either party may commence proceedings in the courts of Queensland, Australia',
          ]} />
          <P>
            Nothing in this clause prevents a party from seeking urgent interlocutory relief from a
            court of competent jurisdiction.
          </P>
        </Section>

        {/* 16 */}
        <Section title="16. Governing Law">
          <P>
            These Terms are governed by and construed in accordance with the laws of Queensland,
            Australia. You submit to the non-exclusive jurisdiction of the courts of Queensland,
            Australia, and any courts that may hear appeals from those courts.
          </P>
        </Section>

        {/* 17 */}
        <Section title="17. Changes to These Terms">
          <P>
            We may update these Terms from time to time. We will notify you of material changes by
            posting the updated Terms on the Platform with a revised &ldquo;Last updated&rdquo;
            date, and where practicable, by email notification at least 14 days before the changes
            take effect.
          </P>
          <P>
            Your continued use of the Platform after the updated Terms take effect constitutes your
            acceptance of the changes. If you do not agree with the updated Terms, you should stop
            using the Platform and close your account.
          </P>
        </Section>

        {/* 18 */}
        <Section title="18. General Provisions">
          <List items={[
            'Severability: if any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.',
            'Waiver: our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.',
            'Entire agreement: these Terms, together with our Privacy Policy, constitute the entire agreement between you and Agentive Property regarding your use of the Platform.',
            'Assignment: you may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations without restriction.',
          ]} />
        </Section>

        {/* 19 */}
        <Section title="19. Contact Us">
          <P>
            If you have questions about these Terms of Service, please contact us:
          </P>
          <P>
            <strong className="text-foreground">Agentive Property</strong>
            <br />
            Email:{' '}
            <a href="mailto:support@agentiveproperty.ai" className="text-accent hover:underline">
              support@agentiveproperty.ai
            </a>
          </P>
        </Section>
      </article>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="landing-container py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">&larr; Back to Home</Link>
          <span>&copy; {new Date().getFullYear()} Agentive Property</span>
        </div>
      </footer>
    </div>
  );
};

/* ─── Reusable typography components ────────────────────────────────────── */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-12 first:mt-0">
    <h2 className="text-xl font-semibold text-foreground mb-4">{title}</h2>
    {children}
  </section>
);

const Sub: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-base font-medium text-foreground mb-2">{title}</h3>
    {children}
  </div>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-base text-muted-foreground leading-relaxed mb-4">{children}</p>
);

const List: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 text-base text-muted-foreground leading-relaxed mb-4">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export default TermsOfService;
